import { useQuery } from "@tanstack/react-query";
import { refetchApiInterval } from "../../shared/constants";
import { getChargerConnectorsEnergyHistogram, getChargerDetails, getChargerHistory, getChargerTransactionList, getChargerTransactions, getChargerTransactionsCount } from "../api/eflow-charger-dashboard-service";
/**
 * React Query hook to fetch charger details by charger ID
 * @param chargerId - The unique identifier for the charger
 * @param projectId - The unique identifier for the project
 * @param enabled - Boolean to enable/disable the query (default: true)
 * @param refetchApiData - Optional counter to force refetch
 * @param enableRefetchInterval - Boolean to enable/disable auto-refetch (default: true)
 * @param timezoneOffset - Optional timezone offset (e.g., "UTC+01:00", "+01:00")
 * @returns React Query result with charger data
 */
export const useChargerDetailsQuery = (chargerId, projectId, enabled = true, refetchApiData, enableRefetchInterval = true, timezoneOffset) => {
    return useQuery({
        enabled: enabled && !!chargerId && !!projectId,
        gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
        queryFn: async () => {
            return await getChargerDetails(chargerId, projectId, timezoneOffset);
        },
        queryKey: ["chargerDetails", chargerId, projectId, refetchApiData, timezoneOffset],
        refetchInterval: enableRefetchInterval ? refetchApiInterval : false,
        refetchOnWindowFocus: false, // Prevent refetch on window focus
        retry: 1,
        staleTime: refetchApiInterval // Align with refetchInterval for consistent caching
    });
};
/**
 * React Query hook to fetch transactions for a specific charger
 * @param assetId - The unique asset identifier for the charger
 * @param projectId - The unique identifier for the project
 * @param queryParams - Optional query parameters (e.g., startDate, endDate, limit, etc.)
 * @param enabled - Boolean to enable/disable the query (default: true)
 * @param refetchApiData - Optional counter to force refetch
 * @returns React Query result with charger transactions data
 */
export const useChargerTransactionsQuery = (assetId, projectId, queryParams, enabled = true, refetchApiData) => {
    return useQuery({
        enabled: enabled && !!assetId && !!projectId,
        gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
        placeholderData: undefined, // Don't show previous data while fetching new data
        queryFn: async () => {
            return await getChargerTransactions(assetId, projectId, queryParams);
        },
        queryKey: ["chargerTransactions", assetId, projectId, queryParams, refetchApiData],
        refetchInterval: refetchApiInterval, // Auto-refresh every 5 minutes
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: refetchApiInterval // Align with refetchInterval for consistent caching
    });
};
/**
 * React Query hook to fetch transaction count for a specific charger
 * @param assetId - The unique asset identifier for the charger
 * @param projectId - The unique identifier for the project
 * @param queryParams - Optional query parameters (e.g., startDate, endDate, etc.)
 * @param enabled - Boolean to enable/disable the query (default: true)
 * @param refetchApiData - Optional counter to force refetch
 * @returns React Query result with transaction count
 */
export const useChargerTransactionsCountQuery = (assetId, projectId, queryParams, enabled = true, refetchApiData) => {
    return useQuery({
        enabled: enabled && !!assetId && !!projectId,
        gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
        placeholderData: undefined, // Don't show previous data while fetching new data
        queryFn: async () => {
            return await getChargerTransactionsCount(assetId, projectId, queryParams);
        },
        queryKey: ["chargerTransactionsCount", assetId, projectId, queryParams, refetchApiData],
        refetchInterval: refetchApiInterval, // Auto-refresh every 5 minutes
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: refetchApiInterval // Align with refetchInterval for consistent caching
    });
};
/**
 * React Query hook to fetch charger history for a specific charger
 * @param assetId - The unique asset identifier for the charger
 * @param projectId - The unique identifier for the project
 * @param queryParams - Optional query parameters (e.g., fromTime, toTime)
 * @param enabled - Boolean to enable/disable the query (default: true)
 * @param refetchApiData - Optional counter to force refetch
 * @returns React Query result with charger history data
 */
export const useChargerHistoryQuery = (assetId, projectId, queryParams, enabled = true, refetchApiData) => {
    return useQuery({
        enabled: enabled && !!assetId && !!projectId,
        gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
        placeholderData: undefined, // Don't show previous data while fetching new data
        queryFn: async () => {
            return await getChargerHistory(assetId, projectId, queryParams);
        },
        queryKey: ["chargerHistory", assetId, projectId, queryParams, refetchApiData],
        refetchInterval: refetchApiInterval, // Auto-refresh every 5 minutes
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: refetchApiInterval // Align with refetchInterval for consistent caching
    });
};
/**
 * React Query hook to fetch energy histogram data for charger connectors
 * @param assetId - The unique asset identifier for the charger
 * @param projectId - The unique identifier for the project
 * @param queryParams - Optional query parameters (e.g., fromTime, toTime)
 * @param enabled - Boolean to enable/disable the query (default: true)
 * @param refetchApiData - Optional counter to force refetch
 * @returns React Query result with energy histogram data
 */
export const useChargerConnectorsEnergyHistogramQuery = (assetId, projectId, queryParams, enabled = true, refetchApiData) => {
    return useQuery({
        enabled: enabled && !!assetId && !!projectId,
        gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
        placeholderData: undefined, // Don't show previous data while fetching new data
        queryFn: async () => {
            return await getChargerConnectorsEnergyHistogram(assetId, projectId, queryParams);
        },
        queryKey: ["chargerConnectorsEnergyHistogram", assetId, projectId, queryParams, refetchApiData],
        refetchInterval: refetchApiInterval, // Auto-refresh every 5 minutes
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: refetchApiInterval // Align with refetchInterval for consistent caching
    });
};
export const useChargerTransactionListQuery = (chargerTransactionSearchRequest, queryParams) => {
    return useQuery({
        enabled: false,
        gcTime: 0,
        queryFn: async () => {
            return await getChargerTransactionList(chargerTransactionSearchRequest, queryParams);
        },
        queryKey: ["chargerTransactionList", queryParams],
        retry: 0
    });
};
